import React, { createContext, useContext, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { AuthState } from './AuthStore';
import { useAuthStore } from './useAuthStore';

interface AuthContextType extends AuthState {
  login: (userInfo: any, token: string) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const { setUser, clearUser, setLoading, ...state } = useAuthStore();

  const loginRef = useRef<(userInfo: any, token: string) => void>();
  const logoutRef = useRef<() => void>();
  const setLoadingRef = useRef<(isLoading?: boolean) => void>();
  const lastTokenRef = useRef<string | null>(null);
  const userDataRef = useRef<any>(null);

  loginRef.current = (userInfo: any, token: string) => {
    const user = {
      name: userInfo.name,
      email: userInfo.email,
      groups: userInfo.groups,
      preferred_username: userInfo.preferred_username,
    };
    setUser(user, token);
  };

  logoutRef.current = () => {
    clearUser();
  };

  setLoadingRef.current = (isLoading: boolean = true) => {
    setLoading(isLoading);
  };

  const login = useCallback((userInfo: any, token: string) => {
    loginRef.current?.(userInfo, token);
  }, []);

  const logout = useCallback(() => {
    logoutRef.current?.();
  }, []);

  useEffect(() => {
    if (authState?.isPending) {
      setLoadingRef.current?.(true);
      return;
    }

    if (authState?.isAuthenticated && authState.accessToken) {
      const currentToken = authState.accessToken.accessToken;

      if (lastTokenRef.current !== currentToken || !userDataRef.current) {
        setLoadingRef.current?.(true);
        oktaAuth
          .getUser()
          .then((userInfo: any) => {
            userDataRef.current = userInfo;
            lastTokenRef.current = currentToken;
            loginRef.current?.(userInfo, currentToken);
          })
          .catch(() => {
            userDataRef.current = null;
            lastTokenRef.current = null;
            logoutRef.current?.();
          });
      } else {
        loginRef.current?.(userDataRef.current, currentToken);
      }
    } else if (authState?.isAuthenticated === false) {
      userDataRef.current = null;
      lastTokenRef.current = null;
      logoutRef.current?.();
    }
  }, [authState, oktaAuth]);

  const refreshToken = useCallback(async () => {
    setLoadingRef.current?.(true);
    try {
      const token = authState?.accessToken?.accessToken;
      if (token && userDataRef.current && lastTokenRef.current === token) {
        loginRef.current?.(userDataRef.current, token);
      } else if (token) {
        const userInfo = await oktaAuth.getUser();
        userDataRef.current = userInfo;
        lastTokenRef.current = token;
        loginRef.current?.(userInfo, token);
      } else {
        setLoadingRef.current?.(false);
      }
    } catch (error) {
      userDataRef.current = null;
      lastTokenRef.current = null;
      setLoadingRef.current?.(false);
      logoutRef.current?.();
    }
  }, [oktaAuth, authState]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
